/**
 * Report des altitudes d'origine sur un itinéraire routé.
 *
 * OSRM ne renvoie que des coordonnées. Sans ce report, une trace routée perd
 * son profil : le dénivelé chute d'environ 19 %, parce qu'un simple plus
 * proche voisin confond les passages superposés d'un parcours qui se croise.
 *
 * L'algorithme procède en trois temps :
 *
 *  1. **appariement monotone** — chaque point d'origine est cherché dans une
 *     fenêtre glissante autour de sa position attendue, et jamais avant le
 *     point déjà apparié. C'est ce qui empêche un croisement de renvoyer vers
 *     l'autre branche ;
 *  2. **déduplication** — plusieurs points d'origine tombant sur le même point
 *     routé, seul le plus proche est retenu comme ancre ;
 *  3. **interpolation** — entre deux ancres proches du tracé d'origine, le
 *     profil d'origine est suivi finement ; là où la route s'en écarte, on
 *     retombe sur un rapport de distance global, faute de correspondance
 *     fiable.
 *
 * Référence : `OsrmRouter.interpolateElevations`.
 */

import { haversine, lowerBound } from './geo'
import type { TrackPoint } from './types'

/**
 * Écart moyen au-delà duquel l'appariement n'est plus jugé fiable.
 *
 * Au-delà, la route emprunte un autre chemin que la trace d'origine : suivre
 * le profil point à point y produirait des marches, alors qu'un rapport de
 * distance global reste cohérent.
 */
const DEVIATION_THRESHOLD_M = 150

/** Bornes de la demi-fenêtre de recherche, en nombre de points routés. */
const HALF_WINDOW_MIN = 100
const HALF_WINDOW_MAX = 500

interface Anchor {
  readonly origIndex: number
  readonly routeIndex: number
  /** Distance entre le point d'origine et le point routé apparié. */
  readonly matchDist: number
}

/** Distances cumulées le long d'une suite de points. */
function cumulativeDistances(points: readonly TrackPoint[]): number[] {
  const distances = new Array<number>(points.length).fill(0)

  for (let i = 1; i < points.length; i += 1) {
    const previous = points[i - 1]!
    const current = points[i]!
    distances[i] =
      distances[i - 1]! +
      haversine(previous.lat, previous.lon, current.lat, current.lon)
  }

  return distances
}

/**
 * Reporte les altitudes des points d'origine sur les points routés.
 *
 * Renvoie les points routés inchangés quand le report n'a pas de sens : moins
 * de deux points, aucune altitude d'origine, ou un profil entièrement plat —
 * un fichier dont toutes les altitudes valent zéro n'en porte pas vraiment.
 */
export function interpolateElevations(
  routePoints: readonly TrackPoint[],
  originalPoints: readonly TrackPoint[],
): TrackPoint[] {
  if (routePoints.length < 2) return [...routePoints]

  const origWithEle = originalPoints.filter((point) => point.ele !== null)
  if (origWithEle.length === 0) return [...routePoints]

  const firstEle = origWithEle[0]!.ele
  if (origWithEle.every((point) => point.ele === firstEle)) {
    return [...routePoints]
  }

  const routeDists = cumulativeDistances(routePoints)
  const origDists = cumulativeDistances(origWithEle)
  const totalRouteDist = routeDists[routeDists.length - 1]!
  const totalOrigDist = origDists[origDists.length - 1]!

  if (totalRouteDist === 0 || totalOrigDist === 0) return [...routePoints]

  /* --- 1. Appariement monotone --- */

  const halfWindow = Math.min(
    Math.max(Math.floor(routePoints.length / 50), HALF_WINDOW_MIN),
    HALF_WINDOW_MAX,
  )

  const rawAnchors: Anchor[] = []
  let previousIdx = 0

  for (const [k, origin] of origWithEle.entries()) {
    const ratio = origDists[k]! / totalOrigDist
    const expectedIdx = Math.floor(ratio * (routePoints.length - 1))

    const searchStart = Math.max(previousIdx, expectedIdx - halfWindow, 0)
    const searchEnd = Math.min(expectedIdx + halfWindow, routePoints.length - 1)

    let bestIdx = searchStart
    let bestDist = Infinity

    for (let i = searchStart; i <= searchEnd; i += 1) {
      const candidate = routePoints[i]!
      const distance = haversine(
        origin.lat,
        origin.lon,
        candidate.lat,
        candidate.lon,
      )
      if (distance < bestDist) {
        bestDist = distance
        bestIdx = i
      }
    }

    rawAnchors.push({ origIndex: k, routeIndex: bestIdx, matchDist: bestDist })
    previousIdx = bestIdx
  }

  /* --- 2. Déduplication par point routé --- */

  const bestPerIndex = new Map<number, Anchor>()
  for (const anchor of rawAnchors) {
    const existing = bestPerIndex.get(anchor.routeIndex)
    if (!existing || anchor.matchDist < existing.matchDist) {
      bestPerIndex.set(anchor.routeIndex, anchor)
    }
  }

  const anchors = [...bestPerIndex.values()].sort(
    (a, b) => a.routeIndex - b.routeIndex,
  )
  if (anchors.length === 0) return [...routePoints]

  const anchorIndices = anchors.map((anchor) => anchor.routeIndex)
  const eleAt = (anchor: Anchor): number => origWithEle[anchor.origIndex]!.ele ?? 0

  /* --- 3. Altitude de chaque point routé --- */

  const first = anchors[0]!
  const last = anchors[anchors.length - 1]!

  return routePoints.map((point, i) => {
    let ele: number

    if (anchors.length === 1) {
      ele = eleAt(first)
    } else if (i <= first.routeIndex) {
      ele = eleAt(first)
    } else if (i >= last.routeIndex) {
      ele = eleAt(last)
    } else {
      const afterPos = insertionPoint(anchorIndices, i)
      const beforePos = afterPos - 1

      if (beforePos < 0) {
        ele = eleAt(anchors[afterPos]!)
      } else {
        const a = anchors[beforePos]!
        const b = anchors[Math.min(afterPos, anchors.length - 1)]!
        const denominator = routeDists[b.routeIndex]! - routeDists[a.routeIndex]!

        if (denominator === 0) {
          ele = eleAt(a)
        } else {
          const routeRatio =
            (routeDists[i]! - routeDists[a.routeIndex]!) / denominator
          const avgMatchDist = (a.matchDist + b.matchDist) / 2

          if (avgMatchDist <= DEVIATION_THRESHOLD_M) {
            const target =
              origDists[a.origIndex]! +
              routeRatio * (origDists[b.origIndex]! - origDists[a.origIndex]!)
            ele = elevationAt(
              origDists,
              origWithEle,
              target,
              a.origIndex,
              b.origIndex,
            )
          } else {
            // La route s'écarte : le rapport de distance global reste la
            // meilleure approximation disponible.
            const target = (routeDists[i]! / totalRouteDist) * totalOrigDist
            ele = elevationAt(origDists, origWithEle, target)
          }
        }
      }
    }

    return { ...point, ele }
  })
}

/** Altitude d'origine à une distance cumulée donnée, par interpolation linéaire. */
function elevationAt(
  origDists: readonly number[],
  origWithEle: readonly TrackPoint[],
  targetDist: number,
  searchFrom = 0,
  searchTo = origWithEle.length - 1,
): number {
  const found = lowerBound(origDists, targetDist, searchFrom, searchTo + 1)
  const j = Math.min(Math.max(found, searchFrom), searchTo)

  if (j <= searchFrom) return origWithEle[searchFrom]!.ele ?? 0
  if (j > searchTo) return origWithEle[searchTo]!.ele ?? 0

  const segment = origDists[j]! - origDists[j - 1]!
  if (segment === 0) return origWithEle[j]!.ele ?? 0

  const t = Math.min(Math.max((targetDist - origDists[j - 1]!) / segment, 0), 1)
  const eleA = origWithEle[j - 1]!.ele ?? 0
  const eleB = origWithEle[j]!.ele ?? 0

  return eleA + (eleB - eleA) * t
}

/** Premier rang dont la valeur atteint la cible. */
function insertionPoint(values: readonly number[], target: number): number {
  let low = 0
  let high = values.length

  while (low < high) {
    const mid = (low + high) >>> 1
    if (values[mid]! < target) low = mid + 1
    else high = mid
  }

  return low
}
